import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    CircularProgress,
    Alert,
    Paper
} from '@mui/material';
import { VideoLibrary } from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import VideoPostCard from './VideoPostCard';

const Watch = () => {
    const [videoPosts, setVideoPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        fetchVideoPosts();
    }, []);

    const fetchVideoPosts = async (pageNum = 1) => {
        try {
            setLoading(true);
            setError('');
            
            const response = await userRequest.get('/contentVideoPost', {
                params: { 
                    page: pageNum, 
                    limit: 12 
                }
            });
            
            const { videoPosts: posts, hasMore: moreAvailable } = response.data;
            
            if (pageNum === 1) {
                setVideoPosts(posts || []);
            } else {
                setVideoPosts(prev => [...prev, ...(posts || [])]);
            }
            
            setHasMore(moreAvailable);
            setPage(pageNum);
        } catch (error) {
            console.error('Error fetching video posts:', error);
            setError('Failed to load video posts.');
        } finally {
            setLoading(false);
        }
    };

    const loadMore = () => {
        if (hasMore && !loading) {
            fetchVideoPosts(page + 1);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <VideoLibrary sx={{ fontSize: 50, color: 'primary.main', mb: 2 }} />
                <Typography 
                    variant="h3" 
                    gutterBottom 
                    sx={{ 
                        fontWeight: 600,
                        color: 'primary.main'
                    }}
                >
                    Watch
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    Improve your listening skills with engaging video content
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && page === 1 ? (
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress size={50} />
                </Box>
            ) : (
                <>
                    {/* Video Posts Grid */}
                    {videoPosts.length > 0 ? (
                        <Grid container spacing={3}>
                            {videoPosts.map((video) => (
                                <Grid item key={video._id} xs={12} sm={6} md={4}>
                                    <VideoPostCard post={video} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                            <VideoLibrary 
                                sx={{ 
                                    fontSize: 80, 
                                    color: 'text.secondary', 
                                    mb: 2 
                                }} 
                            />
                            <Typography 
                                variant="h5" 
                                gutterBottom 
                                color="text.secondary"
                            >
                                No videos available yet
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Check back soon for new video content!
                            </Typography>
                        </Paper>
                    )}

                    {/* Load More */}
                    {hasMore && videoPosts.length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Box
                                component="button"
                                onClick={loadMore}
                                disabled={loading}
                                sx={{
                                    px: 4,
                                    py: 2,
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    border: 'none',
                                    borderRadius: 2,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        backgroundColor: loading ? 'primary.main' : 'primary.dark',
                                        transform: loading ? 'none' : 'translateY(-1px)',
                                    }
                                }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                                        Loading...
                                    </>
                                ) : (
                                    'Load More Videos'
                                )}
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Container>
    );
};

export default Watch; 